/**
 * Weight-map helpers for the weights-preset editor.
 *
 * A preset stores a sparse `{ slug: weight }` map; criteria missing from it
 * fall back to `weight_default`. These helpers always operate on the FULL,
 * resolved set of criteria so editing/normalising is total (no slug silently
 * keeps a stale value).
 *
 * Scoring uses a raw Σ(score × weight), so weights summing to 1.0 keep every
 * product total on the 0–5 scale. The editor surfaces the live sum and offers
 * normalisation; it does not force it (deliberate, per migration 0039).
 */

import type { EvaluationCriterionRow } from "@/infrastructure/supabase/database.types";

/** Resolve the full effective weight map: preset value → criterion baseline. */
export function effectiveWeights(
  criteria: EvaluationCriterionRow[],
  presetWeights: Record<string, number>
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const c of criteria) {
    out[c.slug] = presetWeights[c.slug] ?? c.weight_default;
  }
  return out;
}

export function sumWeights(weights: Record<string, number>): number {
  return Object.values(weights).reduce((acc, w) => acc + w, 0);
}

/** Proportionally rescale every weight so the set sums to exactly 1.0. */
export function normalizeAll(weights: Record<string, number>): Record<string, number> {
  const total = sumWeights(weights);
  if (total <= 0) return { ...weights };
  const out: Record<string, number> = {};
  for (const [slug, w] of Object.entries(weights)) {
    out[slug] = round4(w / total);
  }
  return out;
}

/**
 * Set one criterion's weight to `value` and rescale the OTHERS proportionally
 * so the whole set sums back to 1.0. This is the single-field edit behaviour
 * (criteria page / per-row edit): the value you typed is honoured exactly, and
 * the remaining weight is redistributed across the rest by their current ratio.
 *
 * `value` is clamped to [0, 0.99] so there is always headroom for the others.
 */
export function setAndRenormalize(
  weights: Record<string, number>,
  slug: string,
  value: number
): Record<string, number> {
  const held = Math.max(0, Math.min(0.99, value));
  const others = Object.entries(weights).filter(([s]) => s !== slug);
  const othersTotal = others.reduce((acc, [, w]) => acc + w, 0);
  const remaining = 1 - held;

  const out: Record<string, number> = { [slug]: round4(held) };
  if (othersTotal <= 0) {
    // No existing weight to scale — distribute the remainder equally.
    const each = others.length > 0 ? remaining / others.length : 0;
    for (const [s] of others) out[s] = round4(each);
  } else {
    const factor = remaining / othersTotal;
    for (const [s, w] of others) out[s] = round4(w * factor);
  }
  return out;
}

function round4(n: number): number {
  return Math.round(n * 1e4) / 1e4;
}
